// Contacts-tab derivations (technical + executive contacts), extracted
// from the AdminPage memos so they can be regression-tested directly.
//
// Two decisions shape this file:
// - 2026-08-03 blank-page incident: a NUMERIC customer_id
//   (pc_ssclientid is an integer column) hit .toLowerCase() in a
//   derivation that runs top-level on EVERY AdminPage render once the
//   lifecycle fetch lands — one bad CRM row unmounted every tab.
//   HARDENING CONTRACT: every value that meets a string method goes
//   through lc() first. The backend now coerces ids server-side; this
//   is the belt.
// - 2026-08-03 two-contact rework (operator decision): the single-scalar
//   gap model could not represent "solid technical contact, missing
//   executive", so gaps are PER KIND (technical.gap / executive.gap)
//   plus customer-level states (base_gap: lapsed_active | no_users) that
//   gate both kinds. UI copy: pc_primarycontact renders as "Technical
//   contact", pc_executivecontact as "Executive contact" — the JSON
//   field names keep their column-derived literals (primary/executive).

// String-coerce anything, lowercase it. null/undefined → "".
const lc = (v) => String(v ?? "").toLowerCase();

// One contact kind's facts over a customer's roster rows.
// gap: null (usable) | "unset" | "inactive" | "multiple". The rendered
// holder is the best available one, so a tombstoned holder still shows
// who it was.
const contactFacts = (rows, flagField) => {
  const flagged = rows.filter(r => r[flagField]);
  const active = flagged.filter(r => r.status_key === "active");
  let gap = null;
  if (flagged.length === 0) gap = "unset";
  else if (active.length === 0) gap = "inactive";
  else if (active.length > 1) gap = "multiple";
  const p = active[0] || flagged[0] || null;
  return {
    gap,
    id: p?.id || "",
    name: p?.name || "",
    email: p?.email || "",
    status: p?.status || "",
    status_key: p?.status_key || "",
    last_login: p?.last_login || null,
    created_on: p?.created_on || null,
  };
};

// One row per customer in the universe. Returns null while either input
// is missing (loading).
export function deriveContactRows(
  accessLifecycle, clientList, entitledSlugs, lapsedActiveSlugs,
) {
  if (!Array.isArray(accessLifecycle) || !Array.isArray(clientList)) return null;
  const bySlug = {};
  accessLifecycle.forEach(r => {
    if (r && r.slug) (bySlug[r.slug] = bySlug[r.slug] || []).push(r);
  });
  // Universe: customers with an ENTITLED tracking row PLUS any lapsed
  // customer that still has Active portal users. null sidecar =
  // universe unavailable → unfiltered fallback.
  const entitled = Array.isArray(entitledSlugs)
    ? new Set(entitledSlugs.map(lc))
    : null;
  const lapsedActive = Array.isArray(lapsedActiveSlugs)
    ? new Set(lapsedActiveSlugs.map(lc))
    : new Set();
  const universe = entitled
    ? clientList.filter(c => {
        const s = lc(c.customer_id);
        return entitled.has(s) || lapsedActive.has(s);
      })
    : clientList;
  return universe.map(c => {
    const slug = c.customer_id;
    const rows = bySlug[slug] || [];
    // Customer-level states gate BOTH contact kinds: lapsed-with-active
    // outranks contact hygiene (the next action is offboarding or
    // renewal, not picking a contact); no_users means there is nothing
    // to pick for either kind.
    let base_gap = null;
    if (lapsedActive.has(lc(slug))) base_gap = "lapsed_active";
    else if (rows.length === 0) base_gap = "no_users";
    return {
      slug,
      // Always a string — meets .toLowerCase() in filter/sort.
      customer: String(c.customer_name || slug || ""),
      base_gap,
      technical: contactFacts(rows, "primary"),
      executive: contactFacts(rows, "executive"),
      active_options: rows
        .filter(r => r.status_key === "active")
        .map(r => ({ id: r.id, name: r.name, email: r.email })),
    };
  });
}

// A kind is USABLE for mailing when the customer has no base gap and the
// kind itself has no gap (exactly one Active holder).
const usable = (row, kind) => !row.base_gap && !row[kind].gap;
export const rowHasAnyGap = (row) =>
  !!(row.base_gap || row.technical.gap || row.executive.gap);

// Filter (four-way view + search) and sort. Views (operator decision
// 2026-08-03): technical = usable technical contact; executive = usable
// executive contact; gaps = any gap of any kind; all = everything.
export function filterSortContactRows(contactRows, {
  contactFilter = "technical", contactSearch = "", contactSort = null,
} = {}) {
  if (!contactRows) return null;
  const q = lc(contactSearch).trim();
  let rows = contactRows.filter(r =>
    (contactFilter === "all" ||
     (contactFilter === "gaps" ? rowHasAnyGap(r)
       : contactFilter === "executive" ? usable(r, "executive")
       : usable(r, "technical"))) &&
    (!q || lc(r.customer).includes(q) ||
     lc(r.technical.email).includes(q) || lc(r.executive.email).includes(q) ||
     lc(r.technical.name).includes(q) || lc(r.executive.name).includes(q))
  );
  const dir = contactSort?.dir === "desc" ? -1 : 1;
  const key = contactSort?.key;
  const val = r => {
    if (key === "customer") return lc(r.customer);
    if (key === "technical") return lc(r.technical.name || r.technical.email);
    if (key === "executive") return lc(r.executive.name || r.executive.email);
    // Engagement columns stay the TECHNICAL contact's — the person whose
    // portal activity we track.
    if (key === "last_login") return r.technical.last_login || "";
    if (key === "created") return r.technical.created_on || "";
    return "";
  };
  rows = [...rows];
  if (key) {
    rows.sort((a, b) => (val(a) < val(b) ? -dir : val(a) > val(b) ? dir : 0));
  } else {
    // Default within every view: customer name.
    rows.sort((a, b) => lc(a.customer).localeCompare(lc(b.customer)));
  }
  return rows;
}

export function contactCounts(contactRows) {
  const rows = contactRows || [];
  return {
    technical: rows.filter(r => usable(r, "technical")).length,
    executive: rows.filter(r => usable(r, "executive")).length,
    gaps: rows.filter(rowHasAnyGap).length,
    all: rows.length,
  };
}

// Copy-all emails for the ACTIVE VIEW (operator decision 2026-08-03,
// per-view): technical view → usable technical emails; executive view →
// usable executive emails; all → the DEDUPED union (one person holding
// both flags appears once — dedupe is case-insensitive on email, first
// casing wins); gaps → nothing. Gap rows never contribute an address in
// any view.
export function usableContactEmails(visibleRows, contactFilter) {
  const rows = visibleRows || [];
  if (contactFilter === "gaps") return [];
  const kinds = contactFilter === "technical" ? ["technical"]
    : contactFilter === "executive" ? ["executive"]
    : ["technical", "executive"];
  const seen = new Set();
  const emails = [];
  rows.forEach(r => {
    kinds.forEach(kind => {
      if (!usable(r, kind)) return;
      const email = r[kind].email;
      if (!email) return;
      const dedupeKey = lc(email);
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      emails.push(email);
    });
  });
  return emails;
}
